import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { IoClose } from "react-icons/io5";
import { fmesh } from "../../../core/globalVariables.js";

const LargePieChart = ({
  geojson,
  selectedMetric,
  handleClosePieButtonClick,
  selectedYearEditing,
  landKeySelectedKey,
}) => {
  const [timeSeriesArray, setTimeSeriesArray] = useState([]);
  const [selectedLandType, setSelectedLandType] = useState(null);
  const chartInstanceRef = useRef(null);
  const canvasRef = useRef(null);

  const timeSeriesArrayRef = useRef([]);

  useEffect(() => {
    timeSeriesArrayRef.current = timeSeriesArray;
  }, [timeSeriesArray]);

  const calculatePercentages = (geojson, property) => {
    const sumTotal = {};
    let grandTotal = 0;

    geojson.features.forEach((feature) => {
      const propertyValue =
        feature.feature_data_simple_timeseries[property]?.[selectedYearEditing];
      const metric =
        feature.feature_data_simple_timeseries[selectedMetric]?.[selectedYearEditing];

      if (propertyValue && typeof propertyValue === "string" && metric !== undefined) {
        const roundedMetric = Math.round(metric);
        sumTotal[propertyValue] = (sumTotal[propertyValue] || 0) + roundedMetric;
        grandTotal += roundedMetric;
      }
    });

    const percentages = {};
    for (const landKey in sumTotal) {
      percentages[landKey] = ((sumTotal[landKey] / grandTotal) * 100).toFixed(2);
    }
    return percentages;
  };

  // Convert color to grey based on luminosity
  const greyOutColor = (hex, factor = 0.5) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const grey = Math.round(0.3 * r + 0.59 * g + 0.11 * b);
    const newR = Math.round(r * (1 - factor) + grey * factor);
    const newG = Math.round(g * (1 - factor) + grey * factor);
    const newB = Math.round(b * (1 - factor) + grey * factor);
    return `rgb(${newR},${newG},${newB})`;
  };

  const hoverPieSegment = (label) => {
    const chart = chartInstanceRef.current;
    if (!chart || !timeSeriesArrayRef.current.length) return;

    const dataset = chart.data.datasets[0];
    const activeIndex = chart.data.labels.indexOf(label);
    if (activeIndex === -1) return;

    dataset.backgroundColor = dataset.backgroundColor.map((color, i) =>
      i === activeIndex
        ? timeSeriesArrayRef.current[i].color
        : greyOutColor(timeSeriesArrayRef.current[i].color, 0.9)
    );

    setSelectedLandType(label);

    chart.setActiveElements([{ datasetIndex: 0, index: activeIndex }]);
    chart.update();
  };

  const clearHover = () => {
    const chart = chartInstanceRef.current;
    if (!chart || !timeSeriesArrayRef.current.length) return;

    chart.data.datasets[0].backgroundColor = timeSeriesArrayRef.current.map(entry => entry.color);

    setSelectedLandType("");

    chart.setActiveElements([]);
    chart.update();
  };

  useEffect(() => {
    if (!geojson || !landKeySelectedKey || !canvasRef.current) return;

    const tempArray = [];
    fmesh.traverse((child) => {
      if (child.isMesh) {
        geojson.features.forEach((feature) => {
          if (child.name === feature.skylark_id) {
            const timeSeriesData =
              feature.feature_data_simple_timeseries[landKeySelectedKey]?.[selectedYearEditing];
            if (timeSeriesData) {
              const r = Math.round(child.material.color.r * 255);
              const g = Math.round(child.material.color.g * 255);
              const b = Math.round(child.material.color.b * 255);
              const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
                .toString(16)
                .slice(1)
                .toUpperCase()}`;
              const uniqueEntry = { timeSeriesData, color: hexColor };

              if (!tempArray.some(
                (entry) =>
                  entry.timeSeriesData === uniqueEntry.timeSeriesData &&
                  entry.color === uniqueEntry.color
              )) {
                tempArray.push(uniqueEntry);
              }
            }
          }
        });
      }
    });

    setTimeSeriesArray(tempArray);

    const ctx = canvasRef.current.getContext("2d");
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    const percentages = calculatePercentages(geojson, landKeySelectedKey);
    const labels = Object.keys(percentages);
    const dataValues = Object.values(percentages);

    const backgroundColors = labels.map(
      (label) => tempArray.find((entry) => entry.timeSeriesData === label)?.color || "#ccc"
    );

    chartInstanceRef.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [{
          data: dataValues,
          backgroundColor: backgroundColors,
          borderWidth: 0,
          borderColor: "transparent",
          hoverOffset: 0,
        }],
      },
      options: {
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        animation: false,
        onHover: (event, chartElement) => {
          if (chartElement.length) {
            const label = chartInstanceRef.current.data.labels[chartElement[0].index];
            hoverPieSegment(label);
            setSelectedLandType(label);
          } else {
            clearHover();
            setSelectedLandType(null);
          }
        },
      },
    });

    return () => {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();
    };
  }, [geojson, landKeySelectedKey, selectedMetric, selectedYearEditing]);

  return (
    <div className="LargepieChartWindow">
      <div className="closelargePieButton" onClick={handleClosePieButtonClick}>
        <IoClose />
      </div>
      <h2 className="largePieChartTitle">PIE CHART</h2>
      <canvas id="largepiechartsummary" className="largepieChart" ref={canvasRef} />
      <div className="largeland_types_accum">
        <h2 className="largePieChartTitle">LAND KEY</h2>

        {Object.entries(calculatePercentages(geojson, landKeySelectedKey))
          .sort(([keyA], [keyB]) => {
            const aNum = /^\d/.test(keyA);
            const bNum = /^\d/.test(keyB);
            if (aNum && !bNum) return -1;
            if (!aNum && bNum) return 1;
            return keyA.localeCompare(keyB, undefined, { numeric: true, sensitivity: "base" });
          })
          .map(([key, value], i) => {
            const capitalisedKey = key.charAt(0).toUpperCase() + key.slice(1);
            const color = timeSeriesArray.find((entry) => entry.timeSeriesData === key)?.color || "#ccc";

            return (
              <div
                key={key}
                className={`land_type_item ${selectedLandType === key ? "selected" : ""}`}
                onMouseEnter={() => hoverPieSegment(key)}
                onMouseLeave={() => clearHover()}
              >
                <div className="color_circle" style={{ backgroundColor: color }} />
                <div className="land_type_item_textLargePie">
                  <p className="land_type_item_key">
                    {capitalisedKey.length > 20 ? `${capitalisedKey.substring(0, 20)}...` : capitalisedKey}
                  </p>
                  <p className="land_type_item_value_large">{value}%</p>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default LargePieChart;
